import MuiBox, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { SystemCssProperties } from '@mui/system/styleFunctionSx';
import * as React from 'react';

import { ColorProp, usePaletteColor } from '../theme/color-utils';
import { BaseProps, deepMerge, enumType, makeEnum } from '../utils';

export type BoxProps = MuiBoxProps &
    BaseProps & {
        bgcolor?: ColorProp;
        borderColor?: ColorProp;
        gap?: number;
        padding?: number | number[];
    } & CSSBoxProps;

type CSSBoxProps = Pick<
    SystemCssProperties,
    | 'width'
    | 'minWidth'
    | 'maxWidth'
    | 'height'
    | 'minHeight'
    | 'maxHeight'
    | 'display'
    | 'flexWrap'
    | 'flex'
    | 'flexBasis'
    | 'flexDirection'
    | 'flexGrow'
    | 'flexShrink'
    | 'justifyContent'
    | 'alignItems'
    | 'alignSelf'
    | 'alignContent'
    | 'overflow'
    | 'overflowX'
    | 'overflowY'
    | 'columnGap'
    | 'rowGap'
    | 'gridTemplateColumns'
    | 'gridColumn'
    | 'm'
    | 'my'
    | 'mx'
    | 'mt'
    | 'mb'
    | 'ml'
    | 'mr'
    | 'p'
    | 'py'
    | 'px'
    | 'pt'
    | 'pb'
    | 'pl'
    | 'pr'
    | 'paddingX'
    | 'paddingY'
    | 'paddingTop'
    | 'paddingBottom'
    | 'paddingLeft'
    | 'paddingRight'
    | 'position'
    | 'visibility'
    | 'zIndex'
    | 'border'
    | 'borderRadius'
    | 'borderBottom'
    | 'borderTop'
    | 'borderRight'
    | 'boxSizing'
    | 'whiteSpace'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'textAlign'
    | 'margin'
    | 'marginTop'
    | 'marginBottom'
    | 'marginLeft'
    | 'marginRight'
    | 'marginX'
    | 'marginY'
    | 'boxShadow'
    | 'fontSize'
    | 'lineHeight'
>;

export interface FlexBoxBaseProps {
    gap?: number;
    stack?: boolean;
    stretch?: boolean | number;
    autoShrink?: boolean;
    hAlign?: LayoutHAlign;
    vAlign?: LayoutVAlign;
}

export interface FlexBoxProps extends BoxProps, FlexBoxBaseProps {}

export const LayoutVAlign = makeEnum(
    'center',
    'space-between',
    'flex-end',
    'flex-start'
);
export const LayoutHAlign = makeEnum(
    'center',
    'space-between',
    'flex-end',
    'flex-start'
);

export type LayoutVAlign = enumType<typeof LayoutVAlign>;
export type LayoutHAlign = enumType<typeof LayoutHAlign>;

export const Box = React.forwardRef<HTMLElement, BoxProps>((props, ref) => {
    const {
        padding: _padding,
        bgcolor: _bgcolor,
        borderColor: _borderColor,
        ownerState: _ownerState,
        display,
        flex,
        flexBasis,
        flexDirection,
        flexGrow,
        flexShrink,
        justifyContent,
        alignItems,
        alignSelf,
        alignContent,
        borderRadius,
        border,
        borderBottom,
        borderTop,
        borderRight,
        flexWrap,
        overflow,
        overflowX,
        overflowY,
        columnGap,
        rowGap,
        gridTemplateColumns,
        gridColumn,
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        position,
        visibility,
        zIndex,
        whiteSpace,
        top,
        left,
        right,
        bottom,
        m,
        my,
        mx,
        mt,
        mb,
        ml,
        mr,
        p,
        py,
        px,
        pt,
        pb,
        pl,
        pr,
        paddingX,
        paddingY,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
        margin,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
        marginY,
        marginX,
        gap,
        textAlign,
        boxShadow,
        fontSize,
        lineHeight,
        sx: _sx = {},
        ...muiProps
    } = props as typeof props & { ownerState?: unknown };

    const backgroundColor = usePaletteColor(_bgcolor);
    const borderColor = usePaletteColor(_borderColor);

    const padding = ((): Pick<
        CSSBoxProps,
        'p' | 'py' | 'px' | 'pt' | 'pb' | 'pl' | 'pr'
    > => {
        if (Array.isArray(_padding)) {
            if (_padding.length === 2) {
                return { py: _padding[0], px: _padding[1] };
            }

            if (_padding.length === 3) {
                return { py: _padding[0], px: _padding[1], pb: _padding[2] };
            }

            if (_padding.length === 4) {
                return {
                    pt: _padding[0],
                    pr: _padding[1],
                    pb: _padding[2],
                    pl: _padding[3]
                };
            }

            throw new Error('Padding array must have a length of 2, 3, or 4');
        }

        return {
            p: p ?? _padding,
            py: py ?? paddingY,
            px: px ?? paddingX,
            pt: pt ?? paddingTop,
            pb: pb ?? paddingBottom,
            pl: pl ?? paddingLeft,
            pr: pr ?? paddingRight
        };
    })();

    return (
        <MuiBox
            ref={ref}
            data-role="box"
            {...muiProps}
            sx={deepMerge(
                {
                    ...padding,
                    display,
                    flex,
                    flexBasis,
                    flexDirection,
                    flexGrow,
                    flexShrink,
                    justifyContent,
                    alignItems,
                    alignSelf,
                    alignContent,
                    columnGap,
                    rowGap,
                    gridTemplateColumns,
                    gridColumn,
                    zIndex,
                    border,
                    borderRadius,
                    borderBottom,
                    borderTop,
                    borderRight,
                    gap,
                    backgroundColor,
                    borderColor,
                    flexWrap,
                    overflow,
                    overflowX,
                    overflowY,
                    width,
                    minWidth,
                    maxWidth,
                    height,
                    minHeight,
                    maxHeight,
                    position,
                    visibility,
                    whiteSpace,
                    left,
                    right,
                    top,
                    bottom,
                    mx: marginX ?? mx ?? ml,
                    mr: marginRight ?? mx ?? mr,
                    m: margin ?? m,
                    my: marginY ?? my,
                    mt: marginTop ?? mt,
                    mb: marginBottom ?? mb,
                    ml: marginLeft ?? ml,
                    textAlign,
                    boxShadow,
                    fontSize,
                    lineHeight
                },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
                (_sx ?? {}) as any
            )}
        />
    );
});

export const FlexBox = React.forwardRef<HTMLElement, FlexBoxProps>(
    (props, ref) => {
        const {
            stack,
            stretch,
            hAlign,
            vAlign,
            autoShrink,
            boxSizing,
            sx,
            gap,
            flex,
            alignItems,
            justifyContent,
            flexDirection,
            ...muiProps
        } = props;

        const flexBoxProps = useFlexBoxProps({
            autoShrink,
            gap,
            hAlign,
            vAlign,
            stack,
            stretch,
            alignItems,
            justifyContent,
            flexDirection,
            flex
        });

        return (
            <Box
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                ref={ref as any}
                sx={{
                    ...flexBoxProps,
                    boxSizing: boxSizing ?? 'border-box',
                    ':focus': {
                        outlineWidth: 'none'
                    },
                    cursor: props.onClick ? 'pointer' : undefined,
                    ...sx
                }}
                {...muiProps}
            />
        );
    }
);

export function useFlexBoxProps(props: FlexBoxProps) {
    const direction = {
        flexDirection: (() => {
            if (props.stack) {
                return 'column';
            }
            if (typeof props.flexDirection === 'string') {
                return props.flexDirection;
            }
            return 'row';
        })()
    } satisfies React.CSSProperties;

    const justifyContent = {
        justifyContent:
            (props.stack ? props.vAlign : props.hAlign) ||
            (typeof props.justifyContent === 'string'
                ? props.justifyContent
                : 'flex-start')
    } satisfies React.CSSProperties;

    const alignItems = {
        alignItems:
            (props.stack ? props.hAlign : props.vAlign) ||
            (typeof props.alignItems === 'string'
                ? props.alignItems
                : 'stretch')
    } satisfies React.CSSProperties;

    const autoShrinkConfig = props.autoShrink
        ? ({
              minWidth: 0,
              width: '100%'
          } satisfies React.CSSProperties)
        : undefined;

    const flex = {
        flex: (() => {
            if (typeof props.stretch === 'boolean') {
                return 1;
            }
            if (props.stretch) {
                return props.stretch;
            }
            if (
                typeof props.flex === 'string' ||
                typeof props.flex === 'number'
            ) {
                return props.flex;
            }
            return '0 1 auto';
        })()
    } satisfies React.CSSProperties;

    const gap = { gap: props.gap ?? 0 } satisfies React.CSSProperties;

    return {
        display: 'flex',
        ...autoShrinkConfig,
        ...direction,
        ...justifyContent,
        ...alignItems,
        ...flex,
        ...gap
    } satisfies React.CSSProperties;
}
